import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { useAppSnackbar } from '../../common/TableDesign';
import ModulePageHeader from '../../common/ModulePageHeader';
import { AccountPicker, SupplierPicker } from '../LedgerPickers';
import DocResultDialog from './DocResultDialog';
import StockLinesEditor from './StockLinesEditor';
import { fmtDate, getErrorMessage, itemLabelOf } from './InventoryShared';

const emptyLine = () => ({ item_id: '', quantity: 1, unit_cost: '' });
const emptyForm = () => ({
  receipt_date: new Date().toISOString().slice(0, 10),
  supplier_id: '',
  grni_account_id: '',
  landed_cost_total: '',
  ap_invoice_id: '',
  ap_invoice_number: '',
  lines: [emptyLine()],
});

// Goods receipts: stock coming in (verified against CreateGoodsReceiptDto).
// grni_account_id is REQUIRED (the clearing leg, relieved when the AP
// invoice arrives); each line needs item + qty + unit_cost. Receipts are
// write-only (no list endpoint) - the created document's number is returned
// and its approve/post/reverse steps run from the result dialog.
const ReceiptsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();

  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  if (!can(RESOURCES.LEDGER.INVENTORY, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Goods Receipts.</Alert>;
  }

  const set = (key, value) => setValues((p) => ({ ...p, [key]: value }));
  const idOf = (d) => d?.receipt_id ?? d?.id;
  const numberOf = (d) => d?.receipt_number || (idOf(d) ? `GRN-${idOf(d)}` : '');

  const submitCreate = async () => {
    if (!values.grni_account_id) {
      showSnackbar('GRNI clearing account is required', 'error');
      return;
    }
    const lines = values.lines.filter((l) => l.item_id || l.quantity);
    if (lines.length < 1) {
      showSnackbar('At least one line is required', 'error');
      return;
    }
    if (lines.some((l) => !l.item_id || !(Number(l.quantity) > 0))) {
      showSnackbar('Each line needs an item and a quantity greater than zero', 'error');
      return;
    }
    if (lines.some((l) => l.unit_cost === '' || !(Number(l.unit_cost) >= 0))) {
      showSnackbar('Each line needs a unit cost (zero or more)', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await ledgerAPI.createInvReceipt({
        receipt_date: values.receipt_date,
        ...(values.supplier_id ? { supplier_id: Number(values.supplier_id) } : {}),
        grni_account_id: Number(values.grni_account_id),
        ...(values.landed_cost_total !== '' ? { landed_cost_total: Number(values.landed_cost_total) } : {}),
        ...(values.ap_invoice_id !== '' ? { ap_invoice_id: Number(values.ap_invoice_id) } : {}),
        ...(values.ap_invoice_number?.trim() ? { ap_invoice_number: values.ap_invoice_number.trim() } : {}),
        lines: lines.map((l) => ({
          item_id: Number(l.item_id),
          quantity: Number(l.quantity),
          unit_cost: Number(l.unit_cost),
        })),
      });
      const doc = res?.data?.data ?? res?.data ?? {};
      setResult({ ...doc, id: idOf(doc) ?? doc.receipt_id });
      setShowCreate(false);
      setValues(emptyForm());
      showSnackbar(`Receipt ${numberOf(doc)} created as DRAFT`, 'success');
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <ModulePageHeader
        title="Goods Receipts"
        subtitle="Stock coming in - supplier deliveries. Posting books DR inventory / CR GRNI and creates FIFO layers."
        actions={canCreate ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => { setValues(emptyForm()); setShowCreate(true); }}>
            New Goods Receipt
          </Button>,
        ] : null}
      />
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Receipts are tracked by document number (returned on creation). Approve then post to update stock;
            posted receipts appear under Reports -&gt; Movement, and unlinked ones under Reports -&gt; GRNI Exposure.
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Goods Receipt</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="date" label="Receipt Date" value={values.receipt_date}
                onChange={(e) => set('receipt_date', e.target.value)} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <SupplierPicker value={values.supplier_id} onChange={(id) => set('supplier_id', id)} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <AccountPicker label="GRNI clearing account" value={values.grni_account_id} onChange={(id) => set('grni_account_id', id)} required helper="Relieved when the AP invoice arrives" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="number" label="Landed Cost Total" value={values.landed_cost_total}
                onChange={(e) => set('landed_cost_total', e.target.value)} inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="number" label="AP Invoice ID" value={values.ap_invoice_id}
                onChange={(e) => set('ap_invoice_id', e.target.value)} inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="AP Invoice Number" value={values.ap_invoice_number} onChange={(e) => set('ap_invoice_number', e.target.value)} />
            </Grid>
            <Grid size={12}>
              <StockLinesEditor lines={values.lines} setLines={(lines) => set('lines', lines)} showUnitCost emptyLine={emptyLine} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocResultDialog
        open={!!result}
        onClose={() => setResult(null)}
        title="Goods receipt"
        doc={result}
        setDoc={setResult}
        numberOf={numberOf}
        statusOf={(d) => d.status}
        api={{
          approve: (id) => ledgerAPI.approveInvReceipt(id),
          post: (id) => ledgerAPI.postInvReceipt(id),
          reverse: (id, d) => ledgerAPI.reverseInvReceipt(id, d),
        }}
        canApprove={canApprove}
        renderSummary={(d) => (
          <>
            <Typography variant="body2" color="text.secondary">{fmtDate(d.receipt_date)}</Typography>
            {(d.lines || []).map((l, i) => (
              <Typography key={l.line_id ?? i} variant="body2">
                {l.item ? itemLabelOf(l.item) : `Item ${l.item_id ?? ''}`} - qty {l.quantity} @ {l.unit_cost}
              </Typography>
            ))}
          </>
        )}
        extraContent={(d, notify) => (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Link AP invoice (price-variance insight, no double posting)</Typography>
            <Grid container spacing={1}>
              <Grid size={5}>
                <TextField
                  fullWidth size="small" type="number" label="AP Invoice ID" value={d.ap_invoice_id ?? ''}
                  onChange={(e) => setResult((p) => ({ ...p, ap_invoice_id: e.target.value }))} inputProps={{ min: 1, step: 1 }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth size="small" label="AP Invoice No." value={d.ap_invoice_number ?? ''}
                  onChange={(e) => setResult((p) => ({ ...p, ap_invoice_number: e.target.value }))}
                />
              </Grid>
              <Grid size={3}>
                <Button
                  size="small" variant="outlined" sx={{ mt: 0.5 }}
                  onClick={async () => {
                    if (!d.ap_invoice_id) {
                      notify('AP invoice ID is required', 'error');
                      return;
                    }
                    try {
                      await ledgerAPI.linkApInvoice(d.id, {
                        ap_invoice_id: Number(d.ap_invoice_id),
                        ...(String(d.ap_invoice_number ?? '').trim() ? { ap_invoice_number: String(d.ap_invoice_number).trim() } : {}),
                      });
                      notify('AP invoice linked', 'success');
                    } catch (error) {
                      notify(getErrorMessage(error), 'error');
                    }
                  }}
                >
                  Link
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      />

      {SnackbarComponent}
    </Box>
  );
};

export default ReceiptsTab;
